import { inject, injectable } from 'inversify';
import { firstValueFrom } from 'rxjs';
import { Commons } from '../commons/commons';
import { LogEntry, LogServiceProvider, Logger } from '../models/logger';
import { LogLevelTypes } from '../types/log.types';
import { BrowserUtils } from '../ui-utils/browser-utils';
import { PromiseUtils } from '../utils/promise.utils';
import { ApiConfig } from './api-config';
import { EnvironmentService } from './environment.service';
import { LightHttp } from './light-http.service';
import { LoginService } from './login.service';
import { WebSocketService } from './websocket.service';


export const CONSOLE_STYLE_TRACE = 'background-color: #8c8c8c; color: white';
export const CONSOLE_STYLE_INFO = 'background-color: #2196F3; color: white';
export const CONSOLE_STYLE_WARN = 'background-color: #d8a91e; color: white';
export const CONSOLE_STYLE_ERROR = 'background-color: #F44336; color: white';

export const CONSOLE_STYLE_SUCCESS = 'background-color: #4CAF50; color: white';

declare var window;

export class FileUploadMapProvider {
    getFileUploadMap: () => any;
}

@injectable()
export class LogService implements LogServiceProvider {
    private url: string;
    private wsId = 'undefined';

    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(WebSocketService) private websocketService: WebSocketService,
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(LoginService) private loginService: LoginService,
        @inject(EnvironmentService) private envService: EnvironmentService,
    ) {

    }

    public initialize() {
        this.setUp();
        this.loginService.onSessionDetails.subscribe({
            next: (sessionDetails) => {
                Logger.setSessionDetails(sessionDetails);
                this.wsId = this.websocketService.getOrCreateStoredWSId();
                Logger.setLoggerService(this);
                Logger.setWSId(this.wsId);
            }
        });
    }

    public getBrowserInfo() {
        return BrowserUtils.getBrowserInfo();
    }

    private setUp() {
        if (!BrowserUtils.hasWindow()) {
            return;
        }

        this.url = this.apiConfig.getHistoryTrackEndpoint();
        try {
            if (window.location.host.startsWith('localhost') || this.envService.env.dev) {
                this.url = this.url.replace(/https:\/\/.*?\//, '/localhost');
            }
        } catch {
            // not needed
        }
    }

    private logQueue: LogEntry[] = [];
    private isFlushing = false;
    private static readonly MAX_QUEUE_SIZE = 100;

    public log(logEntry: LogEntry) {
        const shouldLog = BrowserUtils.hasWindow() && LogLevelTypes.isLogTypeEnabled(LogLevelTypes.logReadableStringToLevel(logEntry.level));
        if (shouldLog && Commons.IS_PROD) {
            if (this.logQueue.length < LogService.MAX_QUEUE_SIZE) {
                this.logQueue.push(logEntry);
            }
            if (!this.isFlushing) {
                this.isFlushing = true;
                setTimeout(() => this.flushQueue(), 2000);
            }
        }
    }

    private static readonly MAX_RETRIES = 3;
    private static readonly RETRY_DELAY_MS = 1000;

    private async flushQueue() {
        while (this.logQueue.length > 0) {
            const entry = this.logQueue.shift();
            await this.sendWithRetry(entry);
        }
        this.isFlushing = false;
        // If new entries arrived during flush, schedule another cycle
        if (this.logQueue.length > 0) {
            this.isFlushing = true;
            setTimeout(() => this.flushQueue(), 2000);
        }
    }

    private async sendWithRetry(entry: LogEntry) {
        for (let attempt = 0; attempt <= LogService.MAX_RETRIES; attempt++) {
            try {
                await firstValueFrom(this.http.post(this.url, entry));
                return;
            } catch {
                if (attempt < LogService.MAX_RETRIES) {
                    await PromiseUtils.delay(LogService.RETRY_DELAY_MS * (attempt + 1));
                }
            }
        }
    }
}
